import * as S from "@styled";
import * as React from "react";

import styled from "styled-components";

import { connect } from "react-redux";

import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';

import {
    restrictToParentElement,
    restrictToVerticalAxis,
 } from '@dnd-kit/modifiers';

import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { CSS } from '@dnd-kit/utilities';

import { DispatchType } from "../../shared/utilities";

import { ValidationError } from "../model/ValidationData";

import { State } from "../model/State";

import {
    addProcessVariable, removeProcessVariable, toggleProcessVariableIsSecret,
    updateProcessVariableName, updateProcessVariableValue, updateProcessVariableMode,
    updateProcessVariableDescription, reorderProcessVariables
} from "../actions/ProcessVariableActions";

import { ParameterInputLanguageModeSelector } from "shared/components/select/Select";
import { Option } from "shared/model/Option";
import MonacoEditor from "../../shared/monaco/components/MonacoEditor";
import { IProcessVariable } from "../model/IProcessData";
import { ParameterEditorMode } from "./ParameterInput";
import { ParametersErrorList } from "./ParametersErrorsList";

import CustomToggle from "../../shared/components/CustomToggle";
import useUserTheme from "../../shared/styles/hooks/useUserTheme";
import { GetProcessVariableErrorId, ProcessVarValidationErrorType } from "../actions/ValidateProcessVariables";
import CreateEnvironmentVariableEditor from "./CreateEnvironmentVariableEditor";

import { loader } from "@monaco-editor/react";
import { languageProvider } from "../../shared/monaco/lang/languageProvider";

import { IDisposable } from "monaco-editor";
import { ICompletionTypes } from "../../shared/monaco/model/model";
import { SchemaType } from "../../environmentVariables/model/VariableSchema";

import { DragHandle, DragHandleContainer } from "./ArrayItem";

export const ProcessVariableItemContainer = styled.div`
    border: 1px solid ${S.Colors.grey_2};
    border-radius: 20px;
    padding: 16px 16px 0px 16px;
    margin-bottom: 16px;
`

const ReadonlyMode = styled.span`
    color: ${S.Colors.grey_1};
    font-size: small;
    margin-right: 10px;
`
const ReadonlyDescription = styled.p`
    padding-left: 16px;
`

export const ProcessVariableValueEditor: React.FC<ProcessVariableValueEditorProps> = (props) => {
    const [theme] = useUserTheme();

    const handleValueInputChange = (newValue: string = '') => {
        props.updateVariableValue(props.processVariable.id, newValue);
    };

    const editorModeOptions: Option[] = [
        {
            label: 'Text',
            value: 'text'
        },
        {
            label: 'Boolean',
            value: 'toggle'
        },
        {
            label: 'Number',
            value: 'integer',
        },
        {
            label: 'JSON',
            value: 'json',
        }
    ];

    const getEditorModeOption = (mode: string) => editorModeOptions.find((options) => options.value === mode);

    const handleModeSelectChange = (option: Option) => {
        props.updateVariableMode && props.updateVariableMode(props.processVariable.id, option.value);
    };

    const handleDescriptionChange = (newValue: string = '') => {
        props.updateVariableDescription && props.updateVariableDescription(props.processVariable.id, newValue);
    };

    const hasValueOrReferenceError = props.valueErrors.length > 0 || props.referenceError;

    return (
        <>
            {props.valueErrors.length > 0 && <ParametersErrorList
                id={GetProcessVariableErrorId(props.processVariable, ProcessVarValidationErrorType.Value)}
                validationErrors={props.valueErrors}
            />}

            {props.referenceError && (
                <>
                    <ParametersErrorList
                        id={GetProcessVariableErrorId(props.processVariable, ProcessVarValidationErrorType.Reference)}
                        validationErrors={[props.referenceError]}
                    />
                    {props.onAddEnvironmentVariable && (
                        <CreateEnvironmentVariableEditor
                            forbiddenNames={props.forbiddenEnvVarNames}
                            addNewVariable={props.onAddEnvironmentVariable}
                            error={props.referenceError}
                        />
                    )}
                </>
            )}

            <S.FlexContainer justifySpaceBetween={true} noWrap={true} gap={14}>
                <S.Label>
                    {props.label}
                </S.Label>
                <S.Label style={{ visibility: props.showModeSelector ? 'visible' : 'hidden' }}>
                    {props.disableEdit || props.onlyEditValue
                        ? <ReadonlyMode>{props.processVariable.mode ?? 'json'}</ReadonlyMode>
                        : <ParameterInputLanguageModeSelector
                            id={props.processVariable.id + "_language-mode-selector"}
                            classNamePrefix="react-select"
                            className="language-mode-selector"
                            options={editorModeOptions}
                            value={getEditorModeOption(props.processVariable.mode ?? 'json')}
                            onChange={handleModeSelectChange}
                            width={"115px"}
                            isDisabled={!props.showModeSelector}
                        />
                    }
                </S.Label>
            </S.FlexContainer>

            <S.FormGroup variant={hasValueOrReferenceError ? S.FormGroupVariant.error : S.FormGroupVariant.primary}>
                {props.processVariable.isSecret ? (
                    <S.Input
                        key={props.processVariable.id + "_editor"}
                        value={props.processVariable.displayValue}
                        onChange={(event) => handleValueInputChange(event.currentTarget.value)}
                        isFullWidth={true}
                        type={"password"}
                    />
                ) : (
                    <S.ExpressionEditorContainer variant={hasValueOrReferenceError ? S.ExpressionEditorContainerState.error : S.ExpressionEditorContainerState.primary}>
                        <MonacoEditor
                            mode={ParameterEditorMode.csharp}
                            key={props.processVariable.id + "_editor"}
                            wordWrap={false}
                            value={props.processVariable.displayValue}
                            onChange={handleValueInputChange}
                            theme={theme}
                        />
                    </S.ExpressionEditorContainer>
                )}
            </S.FormGroup>
            {!props.onlyEditValue &&
                <S.FormGroup $inline={true} $gap={16}>
                    <CustomToggle
                        key={props.processVariable.id}
                        checked={props.processVariable.isSecret}
                        onChange={() => props.toggleVariableIsSecret && props.toggleVariableIsSecret(props.processVariable.id)}
                        disabled={props.disableEdit}
                        $inline={true}
                    />
                    <span>Secret</span>
                </S.FormGroup>
            }

            {!props.hideDescription && (
                <S.FormGroup>
                    <S.Label>Description</S.Label>
                    {!props.disableEdit && !props.onlyEditValue
                        ? <S.TextArea
                            value={props.processVariable.description}
                            onChange={(event) => handleDescriptionChange(event.currentTarget.value)}
                            maxLength={1000}
                        />
                        : <ReadonlyDescription>{props.processVariable.description
                            ? props.processVariable.description
                            : "No description."}
                        </ReadonlyDescription>
                    }
                </S.FormGroup>
            )}
        </>
    );
};

interface ProcessVariableValueEditorProps {
    label: string;
    processVariable: IProcessVariable;
    valueErrors: ValidationError[];
    referenceError: ValidationError | undefined;
    updateVariableValue: (id: number, newValue: string) => void;
    updateVariableMode?: (id: number, newMode: string) => void;
    updateVariableDescription?: (id: number, newDescription: string) => void;
    toggleVariableIsSecret?: (id: number) => void;
    disableEdit: boolean | undefined;
    forbiddenEnvVarNames?: string[];
    onAddEnvironmentVariable?: (type: SchemaType, name: string, parentName: string | undefined, value: string | boolean | string[] | undefined) => void;
    onlyEditValue: boolean;
    showModeSelector: boolean;
    hideDescription?: boolean;
}

export const ProcessVariablesPanel: React.FC<ProcessVariablesPanelProps> = (props) => {
    const mountedRef = React.useRef(false);
    const disposableLanguageProviderRef = React.useRef<IDisposable | null>(null);

    const registerLanguageProvider = async () => {
        const initializedMonaco = await loader.init();

        const completionTypes: ICompletionTypes = {
            environmentVariables: props.environmentVariables,
            processReferences: [],
            returnVariables: [],
            triggers: [],
            variables: [],
            snippets: [],
            processVariables: [],
            apiSpecPaths: [],
        }

        disposableLanguageProviderRef.current = languageProvider.registerLanguageProvider(initializedMonaco, completionTypes);
    };

    const disposeLanguageProvider = () => {
        disposableLanguageProviderRef.current?.dispose();
        disposableLanguageProviderRef.current = null;
    };

    React.useEffect(() => {
        if (!mountedRef.current) {
            registerLanguageProvider();
        }

        mountedRef.current = true;

        return () => disposeLanguageProvider();
    }, []);

    React.useEffect(() => {
        if (mountedRef.current && disposableLanguageProviderRef.current) {
            disposeLanguageProvider();
            registerLanguageProvider();
        }
    }, [props.environmentVariables]);

    const getReorderableItemIds = () => props.processVariables.map(pv => pv.id.toString());

    const [reorderableItemids, setReorderableItemIds] = React.useState(getReorderableItemIds());

    React.useEffect(() => {
        setReorderableItemIds(getReorderableItemIds());
    }, [props.processVariables]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    function handleReorderVariables(event: any) {
        const { active, over } = event;

        if (active.id !== over.id) {
            props.reorderProcessVariables(active.id, over.id);
        }
    }

    return <S.ParameterFieldset disabled={props.disableEdit}>
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleReorderVariables}
            modifiers={[restrictToParentElement, restrictToVerticalAxis]}
        >
            <SortableContext
                disabled={props.disableEdit}
                items={reorderableItemids}
                strategy={verticalListSortingStrategy}
            >
                {props.processVariables.map((processVariable) => {
                    return <SortableProcessVariableItem
                        key={processVariable.id}
                        processVariable={processVariable}
                        validationErrors={props.validationErrors ?? []}
                        removeVariable={() => props.removeVariable(processVariable.id)}
                        updateVariableName={(newName: string) => props.updateVariableName(processVariable.id, newName)}
                        updateVariableValue={props.updateVariableValue}
                        updateVariableMode={props.updateVariableMode}
                        updateVariableDescription={props.updateVariableDescription}
                        toggleVariableIsSecret={props.toggleVariableIsSecret}
                        disableEdit={props.disableEdit}
                    />
                })}
            </SortableContext>
        </DndContext>
        <S.FormGroup id="add-process-variable">
            <S.Button onClick={() => props.addVariable()} title={"Add Process variable"}>Add Process variable</S.Button>
        </S.FormGroup>
    </S.ParameterFieldset>;
}

 const SortableProcessVariableItem: React.FC<SortableProcessVariableItemProps> = (props) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id: props.processVariable.id.toString() });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const [isHovered, setIsHovered] = React.useState(false);

    const nameErrors = props.validationErrors.filter(validationError =>
        validationError.elementId === GetProcessVariableErrorId(props.processVariable, ProcessVarValidationErrorType.Name));
    const valueErrors = props.validationErrors.filter(validationError =>
        validationError.elementId === GetProcessVariableErrorId(props.processVariable, ProcessVarValidationErrorType.Value));
    const referenceError = props.validationErrors.find(validationError =>
        validationError.elementId === GetProcessVariableErrorId(props.processVariable, ProcessVarValidationErrorType.Reference));

    return (
        <ProcessVariableItemContainer
            ref={setNodeRef}
            style={style}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <DragHandleContainer {...attributes} {...listeners} justifySpaceBetween={true}>
                <DragHandle />
            </DragHandleContainer>

            {nameErrors.length > 0 && <ParametersErrorList id={GetProcessVariableErrorId(props.processVariable, ProcessVarValidationErrorType.Name)} validationErrors={nameErrors} />}

            <S.FormGroup>
                <S.FormGroup variant={nameErrors.length > 0 ? S.FormGroupVariant.error : S.FormGroupVariant.primary}>
                    <S.FlexContainer justifySpaceBetween={true} centerItems={true}>
                        <S.Label>{FRENDS.Localizations.Name}</S.Label>
                        <S.Button variant={S.ButtonType.text} style={{"padding": "0.5em"}} onClick={props.removeVariable} title={FRENDS.Localizations.Remove}>
                            <i className="ci-trash_full" />
                        </S.Button>
                    </S.FlexContainer>
                    <S.Input
                        isFullWidth={true}
                        value={props.processVariable.name}
                        onChange={(event) => props.updateVariableName(event.target.value)}
                        id={"process-variable-name-input-" + props.processVariable.id}
                        maxLength={400}
                    />
                </S.FormGroup>
                <ProcessVariableValueEditor
                    label="Value"
                    processVariable={props.processVariable}
                    valueErrors={valueErrors}
                    referenceError={referenceError}
                    updateVariableValue={props.updateVariableValue}
                    updateVariableMode={props.updateVariableMode}
                    updateVariableDescription={props.updateVariableDescription}
                    toggleVariableIsSecret={props.toggleVariableIsSecret}
                    disableEdit={props.disableEdit}
                    onlyEditValue={false}
                    showModeSelector={isHovered}
                />
            </S.FormGroup>
        </ProcessVariableItemContainer>
    );
}

interface SortableProcessVariableItemProps {
    processVariable: IProcessVariable;
    validationErrors: ValidationError[];
    disableEdit?: boolean;
    removeVariable: () => void;
    updateVariableName: (newName: string) => void;
    updateVariableValue: (id: number, newValue: string) => void;
    updateVariableMode?: (id: number, newMode: string) => void;
    updateVariableDescription?: (id: number, newDescription: string) => void;
    toggleVariableIsSecret?: (id: number) => void;
}

interface ProcessVariablesPanelOwnProps {
    disableEdit: boolean | undefined;
}

interface ProcessVariablesPanelStateProps {
    processVariables: IProcessVariable[];
    validationErrors: ValidationError[] | undefined;
    environmentVariables: string[];
}

interface ProcessVariablePanelDispatchProps {
    addVariable: () => void;
    updateVariableName: (id: number, newName: string) => void;
    updateVariableMode: (id: number, newMode: string) => void;
    updateVariableDescription: (id: number, newDescription: string) => void;
    removeVariable: (id: number) => void;
    updateVariableValue: (id: number, newValue: string) => void;
    toggleVariableIsSecret: (id: number) => void;
    reorderProcessVariables: (oldIndex: number, newIndex: number) => void;
}

type ProcessVariablesPanelProps = ProcessVariablesPanelOwnProps & ProcessVariablesPanelStateProps & ProcessVariablePanelDispatchProps;

function mapStateToProps(state: State): ProcessVariablesPanelStateProps {
    return {
        processVariables: state.processVariables,
        validationErrors: state.validation.validationDictionary["processVariables"],
        environmentVariables: state.environmentVariables,
    };
}

const mapDispatchToProps = (dispatch: DispatchType<State>): ProcessVariablePanelDispatchProps => {
    return {
        addVariable: () => dispatch(addProcessVariable()),
        updateVariableName: (id, newName) => dispatch(updateProcessVariableName(id, newName)),
        updateVariableValue: (id, newValue) => dispatch(updateProcessVariableValue(id, newValue)),
        updateVariableMode: (id, newMode) => dispatch(updateProcessVariableMode(id, newMode)),
        updateVariableDescription: (id, newDescription) => dispatch(updateProcessVariableDescription(id, newDescription)),
        removeVariable: (id) => dispatch(removeProcessVariable(id)),
        toggleVariableIsSecret: (id) => dispatch(toggleProcessVariableIsSecret(id)),
        reorderProcessVariables: (oldIndex, newIndex) => dispatch(reorderProcessVariables(oldIndex, newIndex))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProcessVariablesPanel) as React.ComponentType<ProcessVariablesPanelOwnProps>;
